import React, { useEffect, useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { Download } from "lucide-react";
import {
  calculateProductValues,
  getCategories,
  fetchProductDynamic,
} from "../../services/productServices";

// Utility to format numbers with commas
const formatNumber = (num) => num.toLocaleString();
// Utility to format date
const formatDate = (isoString) =>
  isoString ? new Date(isoString).toLocaleDateString() : "N/A";

// Full width summary card
const SummaryCard = ({ label, value, exchangeRate }) => {
  const usdValue =
    exchangeRate != null
      ? (value * exchangeRate).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "…";
  return (
    <div className="col-span-full flex-col items-center gap-3 bg-white rounded-2xl shadow-lg p-3 mb-6">
      <h2 className="text-2xl font-semibold">{label}</h2>
      <p className="text-xl">ETB {formatNumber(value)}</p>
      <p className="text-xl">USD {usdValue}</p>
    </div>
  );
};
// Card component for each product
const ProductCard = ({ val, exchangeRate }) => {
  const priceETB = Number(val.price);
  const estETB = Number(val.estimated_total_value);
  const priceUSD =
    exchangeRate != null
      ? (priceETB * exchangeRate).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "…";
  const estUSD =
    exchangeRate != null
      ? (estETB * exchangeRate).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "…";

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 flex flex-col justify-between">
      <div>
        <h2 className="text-xl font-semibold mb-2 border-b ">{val.name}</h2>
        {/* <p className="text-gray-500 text-sm mb-4">Category: {val.category}</p> */}
        <ul className="space-y-1">
          <li>
            <strong>Quantity in Quintal:</strong>{" "}
            {formatNumber(val.total_quintal)}
          </li>
          <li>
            <strong>Price Per Quintal (ETB):</strong> ETB{" "}
            {priceETB.toLocaleString()}
          </li>

          <li>
            <strong>Value in ETB:</strong> ETB {estETB.toLocaleString()}
          </li>
          <li>
            <strong>Value in USD:</strong> USD {estUSD}
          </li>
        </ul>
      </div>
      <p className="text-gray-400 text-xs mt-4">
        Updated: {formatDate(val.updatedAt)}
      </p>
    </div>
  );
};

export default function ProductValueCalc() {
  const [values, setValues] = useState([]);
  const [filteredValues, setFilteredValues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({
    category: "",
    startDate: "",
    endDate: new Date().toISOString().split("T")[0],
    productId: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exchangeRate, setExchangeRate] = useState(null);

  // Fetch data functions
  const exportToExcel = () => {
    const exportData = filteredValues.map((val) => ({
      "Product Name": val.name,
      "Total Quantity (quintal)": val.total_quintal,
      "Unit Price (ETB)": val.price,
      "Estimated Total Value": val.estimated_total_value,
      "Last Updated": formatDate(val.updatedAt),
      Category: val.category,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Product Values");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `Product_Values_${Date.now()}.xlsx`);
  };

  const fetchExchangeRate = async () => {
    try {
      const res = await fetch(
        "https://currency-conversion-and-exchange-rates.p.rapidapi.com/latest?base=ETB&symbols=USD",
        {
          method: "GET",
          headers: {
            "x-rapidapi-key":
              "f7dba3c1d5msh3029e3b69d63aaep10f347jsn79ce51586bb1",
            "x-rapidapi-host":
              "currency-conversion-and-exchange-rates.p.rapidapi.com",
          },
        }
      );
      const data = await res.json();
      setExchangeRate(data.rates?.USD ?? null);
    } catch (err) {
      console.error("Failed to fetch exchange rate", err);
    }
  };

  const fetchValues = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await calculateProductValues(filters);
      if (Array.isArray(result)) {
        setValues(result);
        setFilteredValues(result);
      } else {
        setValues([]);
        setFilteredValues([]);
        setError(result.message || "No data found.");
      }
    } catch (err) {
      setError("Failed to load product values.");
      console.error(err);
    }

    setLoading(false);
  };

  const fetchCategories = async () => {
    try {
      const cats = await getCategories();
      setCategories(cats || []);
    } catch (err) {
      console.error("Failed to fetch categories", err);
    }
  };

  const fetchProducts = async (categoryName) => {
    if (!categoryName) {
      setProducts([]);
      return;
    }
    try {
      const result = await fetchProductDynamic(categoryName);
      setProducts(result || []);
    } catch (err) {
      console.error("Failed to fetch products", err);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCategories();
    fetchValues();
    fetchExchangeRate();
  }, []);

  // Refetch on filter change
  useEffect(() => {
    fetchValues();
  }, [filters]);

  // Fetch products when category changes
  useEffect(() => {
    fetchProducts(filters.category);
  }, [filters.category]);

  // Search filter
  useEffect(() => {
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      setFilteredValues(
        values.filter((val) => val.name.toLowerCase().includes(lower))
      );
    } else setFilteredValues(values);
  }, [searchTerm, values]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "category" ? { productId: "" } : {}),
    }));
  };

  // Client-side filtering & search
  const filteredValuess = useMemo(
    () =>
      values
        .filter(
          (v) =>
            (!filters.category || v.category === filters.category) &&
            (!filters.productId || v.productId === Number(filters.productId))
        )
        .filter((v) =>
          !searchTerm
            ? true
            : v.name.toLowerCase().includes(searchTerm.toLowerCase())
        ),
    [values, filters, searchTerm]
  );

  const summaryLabel = useMemo(() => {
    if (filteredValuess.length === 0) return "Total Estimated Value";

    const sortedByDate = [...filteredValuess].sort(
      (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt)
    );

    const oldestDate = sortedByDate[0]?.updatedAt
      ? new Date(sortedByDate[0].updatedAt).toISOString().split("T")[0]
      : null;

    const startDate = filters.startDate || oldestDate;
    const endDate = filters.endDate || new Date().toISOString().split("T")[0];

    return `Total Value From ${startDate} to ${endDate}`;
  }, [filters, filteredValuess]);

  // Compute total
  const totalEstimatedValue = useMemo(
    () =>
      filteredValuess.reduce(
        (acc, v) => acc + (v.estimated_total_value || 0),
        0
      ),
    [filteredValuess]
  );

  const dateRangeText =
    filters.startDate && filters.endDate
      ? `${filters.startDate} to ${filters.endDate}`
      : "for the past 2 weeks";

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <h1 className="text-3xl font-bold mb-6">Product Value Calculations</h1>

      {/* Filters and export */}
      <div className="flex flex-wrap gap-4 mb-6">
        <select
          name="category"
          value={filters.category}
          onChange={handleFilterChange}
          className="border border-gray-300 rounded-lg py-1 px-3"
        >
          <option value="">All Categories</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.name}>
              {cat.name}
            </option>
          ))}
        </select>
        <select
          name="productId"
          value={filters.productId}
          onChange={handleFilterChange}
          className="border border-gray-300 rounded-lg py-1 px-3"
        >
          <option value="">All Products</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          name="startDate"
          value={filters.startDate}
          onChange={handleFilterChange}
          className="border border-gray-300 rounded-lg py-1 px-3"
        />
        <input
          type="date"
          name="endDate"
          value={filters.endDate}
          onChange={handleFilterChange}
          className="border border-gray-300 rounded-lg py-1 px-3"
        />
        {/* <input
          type="text"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-300 rounded-lg py-1 px-3 flex-grow"
        /> */}
        <button
          onClick={exportToExcel}
          className="flex items-center gap-2 bg-transparent text-black border-2 border-green-400 shadow-md px-4 py-2 rounded hover:bg-green-700 hover:text-white transition-all"
        >
          <Download className="w-4 h-4" />
          Export to Excel
        </button>
      </div>

      {/* Summary */}
      {/* <div className="mb-4 text-lg font-medium">
        Total Estimated value {dateRangeText} is{" "}
        <span className="font-bold">
          ETB {formatNumber(totalEstimatedValue)}
        </span>
      </div> */}

      {/* Content grid */}
      {loading ? (
        <div>Loading product values...</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : filteredValues.length === 0 ? (
        <div>No product values found.</div>
      ) : (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <SummaryCard
            label={summaryLabel}
            value={totalEstimatedValue}
            exchangeRate={exchangeRate}
          />

          {filteredValues.map((val, index) => (
            <ProductCard
              key={`${val.productId}-${index}-${val.name}`}
              val={val}
              exchangeRate={exchangeRate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
